import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { Users, ChatCircleDots, Newspaper, Buildings, Envelope, ArrowRight, ArrowSquareOut, CreditCard, ChartLineUp, Megaphone } from "@phosphor-icons/react";

// All tiles are clickable. Layout: top row = "Manage", second row = "Platform ops".
const manageTiles = [
  { key: "nurseries", label: "Nursery accounts", icon: Buildings, to: "/admin/users", accent: "accent", hint: "Edit, suspend, or open any nursery" },
  { key: "applications_pending", label: "Pending applications", icon: Envelope, to: "/admin/applications", accent: "warm", hint: "Review founding-nursery applicants" },
  { key: "blog_published_total", label: "Blog posts live", icon: Newspaper, to: "/admin/blog", formatter: (s) => `${s.blog_published_total}/${s.blog_posts_total}`, hint: "Write & publish posts" },
];

const opsTiles = [
  { key: "users", label: "All users", icon: Users, to: "/admin/users", hint: "Every account, founding + standard" },
  { key: "subscriptions_active", label: "Subscriptions", icon: CreditCard, to: "/admin/subscriptions", hint: "Plans, MRR & churn", formatter: (s) => s.subscriptions_active ?? s.nurseries },
  { key: "leads_total", label: "Customer leads", icon: ChatCircleDots, to: "/admin/leads", hint: "All inbound leads across nurseries" },
  { key: "mrr", label: "Financials", icon: ChartLineUp, to: "/admin/finance", hint: "AI cost vs subscription revenue", formatter: (s) => `$${(s.mrr ?? 0).toLocaleString()}` },
  { key: "marketing", label: "Marketing", icon: Megaphone, to: "/admin/marketing", hint: "Channels, sources & campaigns", formatter: () => "Open" },
];

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  return (
    <div data-testid="admin-overview-page" className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Platform pulse</div>
          <h1 className="page-title" style={{ marginTop: 8 }}>Overview</h1>
          <p style={{ color: "var(--muted-fg)", marginTop: 8, fontSize: 15 }}>Everything happening across NurserySense. Every tile is clickable.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="btn btn-outline" data-testid="visit-public-site" style={{ minHeight: 40 }}>
          <ArrowSquareOut size={14} weight="bold" /> Visit public site
        </a>
      </div>

      {!stats ? (
        <div className="card" style={{ padding: 24, marginTop: 24 }}>Loading…</div>
      ) : (
        <>
          {/* Manage row */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)", marginTop: 28, marginBottom: 12 }}>Manage</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {manageTiles.map((t) => (
              <TileLink key={t.key} t={t} stats={stats} />
            ))}
          </div>

          {/* Platform ops row */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)", marginTop: 32, marginBottom: 12 }}>Platform ops</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {opsTiles.map((t) => (
              <TileLink key={t.key} t={t} stats={stats} variant="ops" />
            ))}
          </div>
        </>
      )}

      {/* Quick actions */}
      <div style={{ marginTop: 36 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)", marginBottom: 12 }}>Quick actions</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          <ActionLink to="/admin/applications" label="Review new applications" icon={Envelope} />
          <ActionLink to="/admin/blog/new" label="Write a new blog post" icon={Newspaper} />
          <ActionLink to="/admin/users" label="Manage nurseries" icon={Users} />
          <ActionLink to="/admin/leads" label="See all customer leads" icon={ChatCircleDots} />
        </div>
      </div>
    </div>
  );
}

function TileLink({ t, stats, variant }) {
  const value = t.formatter ? t.formatter(stats) : (stats[t.key] ?? 0);
  const Icon = t.icon;
  const isOps = variant === "ops";
  return (
    <Link
      to={t.to}
      className={`stat-tile ${t.accent || ""}`}
      style={{ textDecoration: "none", color: "inherit", cursor: "pointer", display: "block" }}
      data-testid={`tile-${t.key}`}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", opacity: t.accent ? 0.85 : 1, color: t.accent ? "inherit" : "var(--muted-fg)" }}>{t.label}</div>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: t.accent ? "rgba(255,255,255,0.18)" : (isOps ? "var(--bg-alt)" : "var(--primary-soft)"), display: "grid", placeItems: "center" }}>
          <Icon size={20} weight={t.accent ? "fill" : "bold"} color={t.accent ? "white" : (isOps ? "var(--text-soft)" : "var(--primary)")} />
        </div>
      </div>
      <div className="font-display" style={{ fontSize: 44, marginTop: 14, lineHeight: 1 }}>{value ?? "—"}</div>
      {t.hint && <div style={{ marginTop: 10, fontSize: 11.5, opacity: 0.8, lineHeight: 1.4 }}>{t.hint}</div>}
      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85, display: "flex", alignItems: "center", gap: 4, fontWeight: 700 }}>
        Open <ArrowRight size={12} weight="bold" />
      </div>
    </Link>
  );
}

function ActionLink({ to, label, icon: Icon }) {
  return (
    <Link to={to} className="card card-hover" style={{ padding: 18, textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--secondary-soft)", display: "grid", placeItems: "center" }}>
        <Icon size={20} weight="bold" color="var(--secondary)" />
      </div>
      <div style={{ flex: 1, fontWeight: 700 }}>{label}</div>
      <ArrowRight size={16} weight="bold" color="var(--muted-fg)" />
    </Link>
  );
}
